// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */ from 'react';

import UniversalNavLink from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalNavLink';
import { Button } from '@zapier/design-system';
import { usageLink } from '@zapier/design-system-patterns/src/components/UniversalNav/constants';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import UniversalSidebarError from './UniversalSidebarError';
import UniversalSidebarSubheaderButton from './UniversalSidebarSubheaderButton';
import UniversalSidebarUsage from './UniversalSidebarUsage/UniversalSidebarUsage';
import useSidebarData from './utils/useSidebarData';
import UniversalSidebar, {
  type Props as UniversalSidebarProps,
  isLinkSelected,
} from './UniversalSidebar';

/** @jsx isolateComponent */

const infinitize = (number: number) => {
  return number === -1 ? Infinity : number;
};

type Props = {
  // The `Diff`ed props are all props that are either passed in
  // or are handled by `UniversalSidebar`'s `defaultProps`.
  ...$Diff<
    UniversalSidebarProps,
    {
      isFullWidth: $PropertyType<UniversalSidebarProps, 'isFullWidth'>,
      isPinned: $PropertyType<UniversalSidebarProps, 'isPinned'>,
      links: $PropertyType<UniversalSidebarProps, 'links'>,
      logoAriaLabel: $PropertyType<UniversalSidebarProps, 'logoAriaLabel'>,
      logoHref: $PropertyType<UniversalSidebarProps, 'logoHref'>,
      renderFooter: $PropertyType<UniversalSidebarProps, 'renderFooter'>,
      skipLinkHref: $PropertyType<UniversalSidebarProps, 'skipLinkHref'>,
    }
  >,
  /**
   * The `id` of the account the user is currently switched to.
   */
  currentAccountId: number,
};

type MaybeCtaProps = {
  numTasks: number,
  numTasksMax: number,
  numZaps: number,
  numZapsMax: number,
  planAmount: number,
};

const MaybeCta = (props: MaybeCtaProps) => {
  const isFree = props.planAmount === 0;
  const isNearTaskLimit = props.numTasks / props.numTasksMax >= 0.8;
  const isNearZapLimit = props.numZaps / props.numZapsMax >= 0.8;
  return (
    (isFree || isNearTaskLimit || isNearZapLimit) && (
      <Button
        casing="uppercase"
        color="grey"
        href="/app/billing/plans"
        isFullWidth={true}
        size="small"
      >
        Upgrade Plan
      </Button>
    )
  );
};

const Styles = labelStyles(__filename, {
  footerWrapper: {
    overflow: 'hidden',
    padding: 10,
  },
});

const FooterWrapper = ({ children }) => (
  <div css={Styles.footerWrapper}>{children}</div>
);

/**
 * The hooked up `UniversalSidebar` component. Hooked up to data,
 * performs business logic, etc.
 */
const UniversalSidebarContainer = ({ currentAccountId, ...props }: Props) => {
  const result = useSidebarData({ accountId: currentAccountId });
  const { loading, data, error } = result;

  const renderedUsageLink = (
    <UniversalNavLink
      href={usageLink.href}
      icon={usageLink.icon}
      isCollapsed={props.isCollapsed}
      isSelected={isLinkSelected(usageLink.href, props.selectedHref)}
      text={usageLink.text}
    />
  );

  const renderFooter = () => {
    if (props.isCollapsed) {
      return renderedUsageLink;
    }
    if (error) {
      return (
        <React.Fragment>
          {renderedUsageLink}
          <FooterWrapper>
            <UniversalSidebarError>{error.message}</UniversalSidebarError>
          </FooterWrapper>
        </React.Fragment>
      );
    }
    if (loading) {
      return null;
    }
    const { plan, usage } = data.account;
    const tiers = plan.tiers;
    const freeTier = tiers && tiers.find(tier => tier.pricePerTask === 0);
    const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
    const normalizedUsage = {
      isPlanMetered: plan.hasMeteredBilling,
      numTasks: usage.tasks.total,
      numFreeTasksMax,
      numTasksMax: infinitize(usage.tasks.limit),
      numZaps: usage.zaps.on,
      numZapsMax: infinitize(usage.zaps.limit),
    };
    return (
      <FooterWrapper>
        <UniversalSidebarUsage
          {...normalizedUsage}
          resetDate={usage.periodEnd}
          title={`${plan.name} Plan`}
        >
          <MaybeCta {...normalizedUsage} planAmount={plan.amount} />
        </UniversalSidebarUsage>
      </FooterWrapper>
    );
  };

  return (
    <UniversalSidebar
      {...props}
      logoAriaLabel="Go to dashboard"
      logoHref="/app/dashboard"
      renderFooter={renderFooter}
      renderSubheader={() => (
        <UniversalSidebarSubheaderButton
          href="/app/editor"
          iconName="formAdd"
          isCollapsed={props.isCollapsed}
          text="Make a Zap"
        />
      )}
    />
  );
};

export default UniversalSidebarContainer;
